import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import constants from '../config';
import { resolveCardImage } from '../utils/resolveImage';
import { arrangeCardsForGrid } from '../utils/arrangeGrid';
import { renderRarityBadge } from '../utils/rarityBadge';
import LazyCardImage from './LazyCardImage';
import { GameContext } from '../contexts/GameContext';
import './AllListings.css';

var SORT_OPTIONS = [
  { value: 'newest', label: 'Newest' },
  { value: 'price-asc', label: 'Price: Low \u2192 High' },
  { value: 'price-desc', label: 'Price: High \u2192 Low' },
  { value: 'best-deals', label: 'Best Deals' },
];

var FOIL_OPTIONS = [
  { value: '', label: 'All Cards' },
  { value: 'true', label: 'Foil Only' },
  { value: 'false', label: 'Non-Foil Only' },
];

// Rarities that are always holo and cannot be foil (from conditions.js)
var NO_FOIL_RARITIES = ['Super Rare', 'Secret Rare', 'Legendary Rare'];

var STORAGE_KEY = 'allListings_filters';

class AllListings extends Component {
  static contextType = GameContext;

  constructor(props) {
    super(props);

    // Restore filters from sessionStorage
    var saved = {};
    try {
      var raw = sessionStorage.getItem(STORAGE_KEY);
      if (raw) saved = JSON.parse(raw);
    } catch (e) { /* ignore */ }

    this.state = {
      groups: [],
      loading: true,
      total: 0,
      page: 1,
      totalPages: 1,
      sort: saved.sort || 'newest',
      sortOpen: false,
      foil: saved.foil || '',
      foilOpen: false,
      rarity: saved.rarity || '',
      rarityOpen: false,
      rarityOptions: [],
      cardSet: saved.cardSet || '',
      cardSetOpen: false,
      cardSetOptions: [],
    };
    this.handleSortChange = this.handleSortChange.bind(this);
    this.handlePageChange = this.handlePageChange.bind(this);
    this.handleFoilChange = this.handleFoilChange.bind(this);
    this.handleRarityChange = this.handleRarityChange.bind(this);
    this.handleCardSetChange = this.handleCardSetChange.bind(this);
    this.toggleSort = this.toggleSort.bind(this);
    this.toggleFoil = this.toggleFoil.bind(this);
    this.toggleRarity = this.toggleRarity.bind(this);
    this.toggleCardSet = this.toggleCardSet.bind(this);
    this.closeDropdowns = this.closeDropdowns.bind(this);
    this.clearFilters = this.clearFilters.bind(this);
  }

  _getFilterCacheKey() {
    return 'alllistings_filters';
  }

  componentDidMount() {
    this._mounted = true;
    this._prevGameSlug = (this.context && this.context.currentGame && this.context.currentGame.slug) || '';
    this.fetchListings(this.state.page);
    // Also fetch full rarity list (no rarity filter) for dropdown options
    this._fetchRarityOptions();
    document.addEventListener('click', this.closeDropdowns);
  }

  componentDidUpdate(prevProps, prevState, prevContext) {
    var prevSlug = this._prevGameSlug || '';
    var currSlug = (this.context && this.context.currentGame && this.context.currentGame.slug) || '';
    if (currSlug !== prevSlug) {
      this._prevGameSlug = currSlug;
      this.setState({ page: 1, rarity: '', cardSet: '', foil: '' }, function() {
        this.fetchListings(1);
        this._fetchRarityOptions();
      }.bind(this));
    }
  }

  componentWillUnmount() {
    this._mounted = false;
    document.removeEventListener('click', this.closeDropdowns);
    this._saveFilters();
  }

  closeDropdowns(e) {
    var newState = {};
    if (this._sortRef && !this._sortRef.contains(e.target)) {
      if (this.state.sortOpen) newState.sortOpen = false;
    }
    if (this._foilRef && !this._foilRef.contains(e.target)) {
      if (this.state.foilOpen) newState.foilOpen = false;
    }
    // Check both desktop and mobile refs (only one is visible at a time)
    var rarityContains = (this._rarityRef && this._rarityRef.contains(e.target)) || (this._mRarityRef && this._mRarityRef.contains(e.target));
    if (!rarityContains && this.state.rarityOpen) {
      newState.rarityOpen = false;
    }
    var cardSetContains = (this._cardSetRef && this._cardSetRef.contains(e.target)) || (this._mCardSetRef && this._mCardSetRef.contains(e.target));
    if (!cardSetContains && this.state.cardSetOpen) {
      newState.cardSetOpen = false;
    }
    if (Object.keys(newState).length > 0) {
      this.setState(newState);
    }
  }

  _saveFilters() {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
        sort: this.state.sort,
        foil: this.state.foil,
        rarity: this.state.rarity,
        cardSet: this.state.cardSet,
      }));
    } catch (e) { /* ignore */ }
  }

  _fetchRarityOptions() {
    var self = this;
    var gameSlug = self.context && self.context.currentGame && self.context.currentGame.slug;
    var url = constants.DOMAIN + '/api/v1/guest/listings?page=1&limit=1&sort=newest';
    if (gameSlug) url += '&game=' + gameSlug;
    // No foil/rarity params — get the full set of rarities
    fetch(url)
      .then(function(r) { return r.json(); })
      .then(function(data) {
        if (!self._mounted) return;
        var update = {};
        if (data.rarities && data.rarities.length > 0) {
          update.rarityOptions = data.rarities;
        }
        if (data.cardSets && data.cardSets.length > 0) {
          update.cardSetOptions = data.cardSets;
        }
        if (Object.keys(update).length > 0) {
          self.setState(update);
        }
      })
      .catch(function() { /* ignore */ });
  }

  fetchListings(page) {
    var self = this;
    self.setState({ loading: true });

    var gameSlug = self.context && self.context.currentGame && self.context.currentGame.slug;
    var url = constants.DOMAIN + '/api/v1/guest/listings?page=' + page
      + '&limit=24&sort=' + self.state.sort;
    if (self.state.foil) url += '&foil=' + self.state.foil;
    if (self.state.rarity) url += '&rarity=' + encodeURIComponent(self.state.rarity);
    if (self.state.cardSet) url += '&cardSet=' + encodeURIComponent(self.state.cardSet);
    if (gameSlug) url += '&game=' + gameSlug;

    fetch(url)
      .then(function(r) { return r.json(); })
      .then(function(data) {
        self.setState({
          groups: data.groups || [],
          total: data.total || 0,
          page: data.page || 1,
          totalPages: data.totalPages || 1,
          loading: false,
        }, function() {
          self._saveFilters();
        });
        window.scrollTo(0, 0);
      })
      .catch(function() {
        self.setState({ groups: [], loading: false });
      });
  }

  handleSortChange(value) {
    var self = this;
    self.setState({ sort: value, sortOpen: false }, function() {
      self.fetchListings(1);
    });
  }

  handlePageChange(newPage) {
    this.fetchListings(newPage);
  }

  handleFoilChange(value) {
    var self = this;
    // If switching to foil-only and current rarity can't be foil, clear it
    var update = { foil: value, foilOpen: false };
    if (value === 'true' && self.state.rarity && NO_FOIL_RARITIES.indexOf(self.state.rarity) !== -1) {
      update.rarity = '';
    }
    self.setState(update, function() {
      self.fetchListings(1);
    });
  }

  handleRarityChange(value) {
    var self = this;
    self.setState({ rarity: value, rarityOpen: false }, function() {
      self.fetchListings(1);
    });
  }

  handleCardSetChange(value) {
    var self = this;
    self.setState({ cardSet: value, cardSetOpen: false }, function() {
      self.fetchListings(1);
    });
  }

  clearFilters() {
    var self = this;
    self.setState({ foil: '', rarity: '', cardSet: '' }, function() {
      self.fetchListings(1);
    });
  }

  toggleSort() {
    this.setState({ sortOpen: !this.state.sortOpen, foilOpen: false, rarityOpen: false, cardSetOpen: false });
  }

  toggleFoil() {
    this.setState({ foilOpen: !this.state.foilOpen, sortOpen: false, rarityOpen: false, cardSetOpen: false });
  }

  toggleRarity() {
    this.setState({ rarityOpen: !this.state.rarityOpen, sortOpen: false, foilOpen: false, cardSetOpen: false });
  }

  toggleCardSet() {
    this.setState({ cardSetOpen: !this.state.cardSetOpen, sortOpen: false, foilOpen: false, rarityOpen: false });
  }

  renderFilterBar() {
    var self = this;
    var foil = this.state.foil;
    var foilOpen = this.state.foilOpen;
    var rarity = this.state.rarity;
    var rarityOpen = this.state.rarityOpen;
    var allRarityOptions = this.state.rarityOptions;
    var cardSet = this.state.cardSet;
    var cardSetOpen = this.state.cardSetOpen;
    var cardSetOptions = this.state.cardSetOptions;

    // When "Foil Only" is active, hide rarities that can't be foil (always holo)
    var rarityOptions = foil === 'true'
      ? allRarityOptions.filter(function(r) { return NO_FOIL_RARITIES.indexOf(r) === -1; })
      : allRarityOptions;

    var currentFoilLabel = 'All Cards';
    FOIL_OPTIONS.forEach(function(opt) {
      if (opt.value === foil) currentFoilLabel = opt.label;
    });
    var currentRarityLabel = rarity || 'Rarity';
    var currentSetLabel = cardSet || 'Set';

    var dropdownBtnBase = 'tw-flex tw-items-center tw-gap-2 tw-rounded-full tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-border tw-border-solid tw-cursor-pointer tw-transition-all tw-duration-200 ';
    var dropdownBtnActive = 'tw-bg-brand-gold tw-text-black tw-border-brand-gold tw-font-bold';
    var dropdownBtnInactive = 'tw-bg-[#f5f5f5] dark:tw-bg-[#1a1a1a] tw-text-[#666] dark:tw-text-[#999] tw-border-[#e0e0e0] dark:tw-border-[#333] hover:tw-border-brand-gold';
    var dropdownMenuBase = 'filter-menu tw-absolute tw-left-0 tw-top-full tw-mt-1 tw-border tw-border-[#e0e0e0] dark:tw-border-[#333] tw-rounded-lg tw-shadow-lg tw-overflow-hidden tw-z-50 tw-min-w-[160px]';
    var dropdownItemActive = 'tw-bg-brand-gold/10 tw-text-brand-gold tw-font-semibold';
    var dropdownItemInactive = 'tw-text-[#1a1a1a] dark:tw-text-white tw-bg-transparent hover:tw-bg-[#f5f5f5] dark:hover:tw-bg-[#252525]';
    var dropdownItemBase = 'tw-block tw-w-full tw-text-left tw-px-4 tw-py-2.5 tw-text-sm tw-border-0 tw-cursor-pointer tw-transition-colors ';

    // Backdrop blur for dark mode glass effect, solid white for light
    var menuBg = { backgroundColor: 'white' };
    if (typeof document !== 'undefined' && document.documentElement.classList.contains('tw-dark')) {
      menuBg = { backgroundColor: 'rgba(26,26,26,0.85)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)' };
    }

    // Gold glow for active filter buttons
    var activeGlow = { boxShadow: '0 0 10px rgba(255,215,0,0.35), 0 0 3px rgba(255,215,0,0.2)' };
    var foilGlow = { background: 'linear-gradient(135deg, #ffd700, #ffaa00, #ffd700)', boxShadow: '0 0 12px rgba(255,215,0,0.45), 0 0 4px rgba(255,215,0,0.25)' };

    // Mobile-only compact styles
    var mBtnBase = 'tw-flex tw-items-center tw-gap-1.5 tw-whitespace-nowrap tw-rounded-full tw-text-xs tw-font-medium tw-border tw-border-solid tw-cursor-pointer tw-transition-all tw-duration-200 tw-px-3 tw-py-1.5 ';

    var hasFilters = foil || rarity || cardSet;
    var chevron = function(isOpen) {
      return (
        <svg className={'tw-w-3 tw-h-3 tw-transition-transform' + (isOpen ? ' tw-rotate-180' : '')} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      );
    };

    return (
      <div className="tw-mb-4">
        {/* ===== DESKTOP: Pills + Dropdowns + Chips (md and up) ===== */}
        <div className="tw-hidden md:tw-block">
          <div className="tw-flex tw-items-center tw-flex-wrap tw-gap-2">
            {/* Foil toggle pills */}
            {FOIL_OPTIONS.map(function(opt) {
              var isActive = foil === opt.value;
              var isFoilActive = isActive && opt.value === 'true';
              return (
                <button
                  key={opt.value}
                  onClick={function() { self.handleFoilChange(opt.value); }}
                  className={'tw-px-4 tw-py-2 tw-rounded-full tw-text-sm tw-font-medium tw-border tw-border-solid tw-cursor-pointer tw-transition-all tw-duration-200 ' +
                    (isFoilActive
                      ? 'tw-text-black tw-border-transparent tw-font-bold'
                      : isActive
                        ? 'tw-bg-brand-gold tw-text-black tw-border-brand-gold tw-font-bold'
                        : 'tw-bg-[#f5f5f5] dark:tw-bg-[#1a1a1a] tw-text-[#666] dark:tw-text-[#999] tw-border-[#e0e0e0] dark:tw-border-[#333] hover:tw-border-brand-gold'
                    )
                  }
                  style={isFoilActive ? foilGlow : isActive ? activeGlow : undefined}
                >
                  {opt.value === 'true' ? (
                    <span className="tw-flex tw-items-center tw-gap-1.5">
                      <svg className="tw-w-3.5 tw-h-3.5" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                      {opt.label}
                    </span>
                  ) : opt.label}
                </button>
              );
            })}

            {/* Rarity dropdown */}
            <div
              className="tw-relative"
              ref={function(el) { self._rarityRef = el; }}
            >
              <button
                onClick={self.toggleRarity}
                className={dropdownBtnBase + (rarity ? dropdownBtnActive : dropdownBtnInactive)}
                style={rarity ? activeGlow : undefined}
              >
                {rarity || 'All Rarities'}
                {chevron(rarityOpen)}
              </button>
              {rarityOpen ? (
                <div className={dropdownMenuBase} style={menuBg}>
                  <button
                    onClick={function() { self.handleRarityChange(''); }}
                    className={dropdownItemBase + (!rarity ? dropdownItemActive : dropdownItemInactive)}
                  >
                    All Rarities
                  </button>
                  {rarityOptions.map(function(r) {
                    return (
                      <button
                        key={r}
                        onClick={function() { self.handleRarityChange(r); }}
                        className={dropdownItemBase + (rarity === r ? dropdownItemActive : dropdownItemInactive)}
                      >
                        {r}
                      </button>
                    );
                  })}
                </div>
              ) : null}
            </div>

            {/* Card Set dropdown */}
            <div
              className="tw-relative"
              ref={function(el) { self._cardSetRef = el; }}
            >
              <button
                onClick={self.toggleCardSet}
                className={dropdownBtnBase + (cardSet ? dropdownBtnActive : dropdownBtnInactive)}
                style={cardSet ? activeGlow : undefined}
              >
                {cardSet || 'All Sets'}
                {chevron(cardSetOpen)}
              </button>
              {cardSetOpen ? (
                <div className={dropdownMenuBase} style={Object.assign({}, menuBg, { maxHeight: '280px', overflowY: 'auto', minWidth: '220px' })}>
                  <button
                    onClick={function() { self.handleCardSetChange(''); }}
                    className={dropdownItemBase + (!cardSet ? dropdownItemActive : dropdownItemInactive)}
                  >
                    All Sets
                  </button>
                  {cardSetOptions.map(function(s) {
                    return (
                      <button
                        key={s}
                        onClick={function() { self.handleCardSetChange(s); }}
                        className={dropdownItemBase + (cardSet === s ? dropdownItemActive : dropdownItemInactive)}
                      >
                        {s}
                      </button>
                    );
                  })}
                </div>
              ) : null}
            </div>
          </div>

          {/* Active filter chips — desktop only */}
          {hasFilters ? (
            <div className="tw-flex tw-items-center tw-flex-wrap tw-gap-2 tw-mt-3">
              {foil ? (
                <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-bg-brand-gold/10 tw-text-brand-gold tw-text-xs tw-font-semibold tw-px-3 tw-py-1.5 tw-rounded-full">
                  {foil === 'true' ? 'Foil Only' : 'Non-Foil Only'}
                  <button
                    onClick={function() { self.handleFoilChange(''); }}
                    className="tw-border-0 tw-bg-transparent tw-text-brand-gold tw-cursor-pointer tw-p-0 tw-leading-none tw-text-sm tw-font-bold hover:tw-opacity-70"
                  >
                    &times;
                  </button>
                </span>
              ) : null}
              {rarity ? (
                <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-bg-brand-gold/10 tw-text-brand-gold tw-text-xs tw-font-semibold tw-px-3 tw-py-1.5 tw-rounded-full">
                  {rarity}
                  <button
                    onClick={function() { self.handleRarityChange(''); }}
                    className="tw-border-0 tw-bg-transparent tw-text-brand-gold tw-cursor-pointer tw-p-0 tw-leading-none tw-text-sm tw-font-bold hover:tw-opacity-70"
                  >
                    &times;
                  </button>
                </span>
              ) : null}
              {cardSet ? (
                <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-bg-brand-gold/10 tw-text-brand-gold tw-text-xs tw-font-semibold tw-px-3 tw-py-1.5 tw-rounded-full">
                  {cardSet}
                  <button
                    onClick={function() { self.handleCardSetChange(''); }}
                    className="tw-border-0 tw-bg-transparent tw-text-brand-gold tw-cursor-pointer tw-p-0 tw-leading-none tw-text-sm tw-font-bold hover:tw-opacity-70"
                  >
                    &times;
                  </button>
                </span>
              ) : null}
              <button
                onClick={self.clearFilters}
                className="tw-text-xs tw-text-[#999] dark:tw-text-[#666] tw-underline tw-cursor-pointer tw-bg-transparent tw-border-0 tw-p-0 hover:tw-text-brand-gold"
              >
                Clear all
              </button>
            </div>
          ) : null}
        </div>

        {/* ===== MOBILE: Compact 3 dropdowns in one row (below md) ===== */}
        <div className="md:tw-hidden">
          <div className="tw-flex tw-items-center tw-gap-2 tw-flex-wrap">
            {/* Foil dropdown */}
            <div
              className="tw-relative"
              ref={function(el) { self._foilRef = el; }}
            >
              <button
                onClick={self.toggleFoil}
                className={mBtnBase + (foil ? dropdownBtnActive : dropdownBtnInactive)}
                style={foil === 'true' ? foilGlow : foil ? activeGlow : undefined}
              >
                {foil === 'true' ? (
                  <svg className="tw-w-3 tw-h-3" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                ) : null}
                {currentFoilLabel}
                {chevron(foilOpen)}
              </button>
              {foilOpen ? (
                <div className={dropdownMenuBase} style={menuBg}>
                  {FOIL_OPTIONS.map(function(opt) {
                    var isActive = foil === opt.value;
                    return (
                      <button
                        key={opt.value || 'all'}
                        onClick={function() { self.handleFoilChange(opt.value); }}
                        className={dropdownItemBase + (isActive ? dropdownItemActive : dropdownItemInactive)}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              ) : null}
            </div>

            {/* Rarity dropdown (mobile) */}
            <div
              className="tw-relative"
              ref={function(el) { self._mRarityRef = el; }}
            >
              <button
                onClick={self.toggleRarity}
                className={mBtnBase + (rarity ? dropdownBtnActive : dropdownBtnInactive)}
                style={rarity ? activeGlow : undefined}
              >
                {currentRarityLabel}
                {chevron(rarityOpen)}
              </button>
              {rarityOpen ? (
                <div className={dropdownMenuBase} style={menuBg}>
                  <button
                    onClick={function() { self.handleRarityChange(''); }}
                    className={dropdownItemBase + (!rarity ? dropdownItemActive : dropdownItemInactive)}
                  >
                    All Rarities
                  </button>
                  {rarityOptions.map(function(r) {
                    return (
                      <button
                        key={r}
                        onClick={function() { self.handleRarityChange(r); }}
                        className={dropdownItemBase + (rarity === r ? dropdownItemActive : dropdownItemInactive)}
                      >
                        {r}
                      </button>
                    );
                  })}
                </div>
              ) : null}
            </div>

            {/* Card Set dropdown (mobile) */}
            <div
              className="tw-relative"
              ref={function(el) { self._mCardSetRef = el; }}
            >
              <button
                onClick={self.toggleCardSet}
                className={mBtnBase + (cardSet ? dropdownBtnActive : dropdownBtnInactive)}
                style={cardSet ? activeGlow : undefined}
              >
                {currentSetLabel}
                {chevron(cardSetOpen)}
              </button>
              {cardSetOpen ? (
                <div className={dropdownMenuBase} style={Object.assign({}, menuBg, { maxHeight: '280px', overflowY: 'auto', minWidth: '220px' })}>
                  <button
                    onClick={function() { self.handleCardSetChange(''); }}
                    className={dropdownItemBase + (!cardSet ? dropdownItemActive : dropdownItemInactive)}
                  >
                    All Sets
                  </button>
                  {cardSetOptions.map(function(s) {
                    return (
                      <button
                        key={s}
                        onClick={function() { self.handleCardSetChange(s); }}
                        className={dropdownItemBase + (cardSet === s ? dropdownItemActive : dropdownItemInactive)}
                      >
                        {s}
                      </button>
                    );
                  })}
                </div>
              ) : null}
            </div>

            {/* Clear filters */}
            {hasFilters ? (
              <button
                onClick={self.clearFilters}
                className="tw-whitespace-nowrap tw-text-xs tw-text-[#999] dark:tw-text-[#666] tw-underline tw-cursor-pointer tw-bg-transparent tw-border-0 tw-p-0 hover:tw-text-brand-gold"
              >
                Clear
              </button>
            ) : null}
          </div>
        </div>
      </div>
    );
  }

  renderSortBar() {
    var sort = this.state.sort;
    var sortOpen = this.state.sortOpen;
    var total = this.state.total;
    var self = this;

    var currentLabel = 'Newest';
    SORT_OPTIONS.forEach(function(opt) {
      if (opt.value === sort) currentLabel = opt.label;
    });

    return (
      <div className="tw-flex tw-items-center tw-justify-between tw-mb-6 tw-flex-wrap tw-gap-3">
        <p className="tw-text-sm tw-text-[#999] dark:tw-text-[#666] tw-mb-0">
          {total} {total === 1 ? 'card' : 'cards'} available
        </p>
        <div
          className="tw-relative"
          ref={function(el) { self._sortRef = el; }}
        >
          <button
            onClick={this.toggleSort}
            className="tw-flex tw-items-center tw-gap-2 tw-bg-[#f5f5f5] dark:tw-bg-[#1a1a1a] tw-border tw-border-[#e0e0e0] dark:tw-border-[#333] tw-rounded-lg tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-[#1a1a1a] dark:tw-text-white tw-cursor-pointer tw-transition-colors hover:tw-border-brand-gold"
          >
            <svg className="tw-w-4 tw-h-4 tw-text-[#999]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12" />
            </svg>
            {currentLabel}
            <svg className={'tw-w-3 tw-h-3 tw-text-[#999] tw-transition-transform' + (sortOpen ? ' tw-rotate-180' : '')} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {sortOpen ? (
            <div className="tw-absolute tw-right-0 tw-top-full tw-mt-1 tw-bg-white dark:tw-bg-[#1a1a1a] tw-border tw-border-[#e0e0e0] dark:tw-border-[#333] tw-rounded-lg tw-shadow-lg tw-overflow-hidden tw-z-50 tw-min-w-[180px]">
              {SORT_OPTIONS.map(function(opt) {
                var isActive = opt.value === sort;
                return (
                  <button
                    key={opt.value}
                    onClick={function() { self.handleSortChange(opt.value); }}
                    className={'tw-block tw-w-full tw-text-left tw-px-4 tw-py-2.5 tw-text-sm tw-border-0 tw-cursor-pointer tw-transition-colors ' +
                      (isActive
                        ? 'tw-bg-brand-gold/10 tw-text-brand-gold tw-font-semibold'
                        : 'tw-text-[#1a1a1a] dark:tw-text-white tw-bg-transparent hover:tw-bg-[#f5f5f5] dark:hover:tw-bg-[#252525]'
                      )
                    }
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  renderGrid() {
    var groups = this.state.groups;

    // Prepare groups for arrangeCardsForGrid (needs horizontal property at top level)
    var prepared = groups.map(function(g) {
      return Object.assign({}, g, { horizontal: g.horizontal === true });
    });
    var arranged = arrangeCardsForGrid(prepared, 4);

    return (
      <div className="tw-grid tw-grid-cols-2 md:tw-grid-cols-4 tw-gap-4 tw-items-stretch">
        {arranged.map(function(group) {
          var imageUrl = resolveCardImage(group.cardImage);
          var isHoriz = group.horizontal === true;
          var displayName = (group.cardName || '').replace(/\s*\(Foil\)/i, '');

          return (
            <Link
              key={(group.cardId || '') + (group.foil ? '_foil' : '')}
              to={'/card/' + group.cardId}
              className={'tw-bg-[#f5f5f5] dark:tw-bg-[#1a1a1a] tw-rounded-xl tw-border tw-border-solid tw-border-[#e0e0e0] dark:tw-border-[#333] tw-overflow-hidden tw-transition-all tw-duration-200 listing-card-hover tw-flex tw-flex-col' + (isHoriz ? ' horiz-card' : '')}
              style={{ display: 'flex', textDecoration: 'none' }}
            >
              <div className={'tw-bg-[#e0e0e0] dark:tw-bg-[#252525] tw-overflow-hidden tw-flex tw-items-center tw-justify-center tw-relative' + (isHoriz ? ' horiz-card-img-tall' : ' tw-aspect-[4/5] tw-py-3')}>
                <LazyCardImage src={imageUrl} alt={displayName} rotate={isHoriz ? (group.rotation || -90) : 0} />

                {/* Deal badge — top right */}
                {group.dealPercent >= 15 ? (
                  <div
                    className="deal-badge-pulse tw-absolute tw-top-2 tw-right-2 tw-text-white tw-text-[10px] tw-font-bold tw-px-2.5 tw-py-1 tw-rounded-full"
                    style={{ backgroundColor: '#059669', boxShadow: '0 1px 4px rgba(0,0,0,0.3)' }}
                  >
                    {group.dealPercent}% Below Market
                  </div>
                ) : null}

                {/* Foil badge — top left */}
                {group.foil ? (
                  <div
                    className="tw-absolute tw-top-2 tw-left-2 tw-text-[10px] tw-font-bold tw-px-2.5 tw-py-1 tw-rounded-full tw-text-black"
                    style={{ background: 'linear-gradient(135deg, #ffd700, #ffaa00, #ffd700)' }}
                  >
                    Foil
                  </div>
                ) : null}

                {/* Price pill — bottom left */}
                <div className="tw-absolute tw-bottom-2 tw-left-2 tw-bg-black/75 tw-rounded-full tw-overflow-hidden">
                  <span className="tw-text-brand-gold tw-text-sm tw-font-bold tw-px-3 tw-py-1">
                    ${group.lowestPrice ? group.lowestPrice.toFixed(2) : '0.00'}
                  </span>
                </div>
              </div>

              <div className="tw-p-3 tw-flex-1">
                <p className="tw-text-sm tw-font-semibold tw-text-[#1a1a1a] dark:tw-text-white tw-mb-0.5 tw-truncate">
                  {displayName}
                </p>
                <p className="tw-text-xs tw-text-[#666] dark:tw-text-[#999] tw-mb-0.5 tw-truncate">
                  {group.listingCount} {group.listingCount === 1 ? 'listing' : 'listings'}
                </p>
                {group.marketPrice ? (
                  <p className="tw-text-xs tw-text-[#666] dark:tw-text-[#999] tw-mb-0.5 tw-truncate">
                    Market: ${group.marketPrice.toFixed(2)}
                    {group.dealPercent > 0 ? (
                      <span className="tw-text-green-500 tw-font-semibold tw-ml-1">
                        {group.dealPercent}% below
                      </span>
                    ) : null}
                  </p>
                ) : null}
                <div className="tw-mt-1" style={{ marginLeft: '-2px' }}>
                  {renderRarityBadge(group.rarity, null)}
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    );
  }

  renderPagination() {
    var page = this.state.page;
    var totalPages = this.state.totalPages;
    var self = this;

    if (totalPages <= 1) return null;

    // Build page numbers: show up to 5 pages centered on current
    var pages = [];
    var startPage = Math.max(1, page - 2);
    var endPage = Math.min(totalPages, startPage + 4);
    if (endPage - startPage < 4) {
      startPage = Math.max(1, endPage - 4);
    }
    for (var i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    var btnBase = 'tw-border tw-border-[#e0e0e0] dark:tw-border-[#333] tw-rounded-lg tw-px-3 tw-py-2 tw-text-sm tw-font-medium tw-cursor-pointer tw-transition-colors';
    var btnInactive = ' tw-bg-[#f5f5f5] dark:tw-bg-[#1a1a1a] tw-text-[#1a1a1a] dark:tw-text-white hover:tw-border-brand-gold';
    var btnActive = ' tw-bg-brand-gold tw-text-black tw-border-brand-gold';
    var btnDisabled = ' tw-bg-[#f5f5f5] dark:tw-bg-[#1a1a1a] tw-text-[#ccc] dark:tw-text-[#444] tw-cursor-default';

    return (
      <div className="tw-flex tw-items-center tw-justify-center tw-gap-2 tw-mt-8">
        <button
          onClick={page > 1 ? function() { self.handlePageChange(page - 1); } : undefined}
          className={btnBase + (page > 1 ? btnInactive : btnDisabled)}
          disabled={page <= 1}
        >
          &larr; Prev
        </button>
        {pages.map(function(p) {
          return (
            <button
              key={p}
              onClick={function() { self.handlePageChange(p); }}
              className={btnBase + (p === page ? btnActive : btnInactive)}
            >
              {p}
            </button>
          );
        })}
        <button
          onClick={page < totalPages ? function() { self.handlePageChange(page + 1); } : undefined}
          className={btnBase + (page < totalPages ? btnInactive : btnDisabled)}
          disabled={page >= totalPages}
        >
          Next &rarr;
        </button>
      </div>
    );
  }

  renderSkeleton() {
    return (
      <div className="tw-grid tw-grid-cols-2 md:tw-grid-cols-4 tw-gap-4">
        {[1, 2, 3, 4, 5, 6, 7, 8].map(function(i) {
          return (
            <div
              key={i}
              className="tw-bg-[#f5f5f5] dark:tw-bg-[#1a1a1a] tw-rounded-xl tw-border tw-border-[#e0e0e0] dark:tw-border-[#333] tw-overflow-hidden"
            >
              <div className="tw-aspect-[4/5] hp-skeleton" />
              <div className="tw-p-3">
                <div className="tw-h-4 tw-w-3/4 hp-skeleton tw-rounded tw-mb-2" />
                <div className="tw-h-3 tw-w-1/2 hp-skeleton tw-rounded" />
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  render() {
    var loading = this.state.loading;
    var groups = this.state.groups;

    return (
      <div className="tw-max-w-6xl tw-mx-auto tw-px-4 tw-py-8">
        <div className="tw-mb-8">
          <h1 className="tw-text-2xl tw-font-bold tw-text-[#1a1a1a] dark:tw-text-white tw-mb-1">
            All Listings
          </h1>
          <p className="tw-text-sm tw-text-[#999] dark:tw-text-[#666] tw-mb-0">
            Browse every active listing. Find the best deals on the marketplace.
          </p>
        </div>

        {this.renderFilterBar()}
        {this.renderSortBar()}

        {loading ? this.renderSkeleton() : groups.length > 0 ? (
          <div>
            {this.renderGrid()}
            {this.renderPagination()}
          </div>
        ) : (
          <div className="tw-text-center tw-py-16 tw-px-4 tw-bg-[#f5f5f5] dark:tw-bg-[#1a1a1a] tw-rounded-2xl tw-border tw-border-[#e0e0e0] dark:tw-border-[#333]">
            <svg className="tw-w-12 tw-h-12 tw-mx-auto tw-text-brand-gold tw-opacity-60 tw-mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <h3 className="tw-text-lg tw-font-bold tw-text-[#1a1a1a] dark:tw-text-white tw-mb-2">
              No Listings Found
            </h3>
            <p className="tw-text-sm tw-text-[#999] dark:tw-text-gray-500">
              {this.state.foil || this.state.rarity || this.state.cardSet
                ? 'No listings match your filters. Try adjusting or clearing them.'
                : 'Check back soon — new cards are listed every day.'}
            </p>
          </div>
        )}
      </div>
    );
  }
}

export default AllListings;
